import type { TFunction } from 'i18next';
import { formatClock } from '@/features/trips/format';
import type { MovedWhileParkedAlert } from './types';

/**
 * A moved-while-parked row (slice 82a) — the ELEVENTH item shape of the alerts feed.
 * A vehicle that had been parked ≥ 15 min moved ≥ 50 m with ignition KNOWN-ON at the
 * moved-to fix: the "someone drove my parked vehicle and I was never told" case Mahad
 * raised (1 Oct). It is a security signal, `severity: 'warning'`, so it counts toward
 * the unread badge — tamper is `critical`, this is a notch below.
 *
 * 🔴 IT IS A POSITIONED ITEM, always. Unlike the power/ignition/tamper/idle pin legs,
 * `observedLat`/`observedLon` are the MOVED-TO fix and are NON-NULL (the backend only
 * emits this alert at a valid fix), so — exactly like `OverspeedRow` / `AfterHoursRow`
 * — it is ALWAYS a select button: selecting it centres the fleet map on the observed
 * point (and marks read), and on vehicle detail (no map) selecting just marks it read.
 * There is NO "last known location" line and never a Link fallback: the location is
 * real, so the row never pretends otherwise.
 *
 * 🔴 IT IS A "Security" ALERT — the same tag/group as tamper (orchestrator, 2 Oct),
 * so the tag reads "Security" (SO reuses the existing tamper tag string) on amber /
 * warning tone. The distance moved (`distanceM`, the frozen detail) renders as
 * "moved N m" beside the observed time WHEN present — older rows may omit it, so the
 * line degrades to the observed time alone. Tokens only; no hex literal enters here.
 */
export function MovedWhileParkedRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: MovedWhileParkedAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: MovedWhileParkedAlert) => void;
  t: TFunction;
}) {
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  // Tolerate an absent distance (older rows): show it only when the wire carried it.
  const distanceM = alert.distanceM;
  const hasDistance = distanceM != null;

  // Warning weight, like AfterHoursRow: attention tone until read, amber tag + dot.
  const titleClass = !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : 'bg-raad-warn';

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(alert)}
        aria-pressed={selected}
        data-testid="moved-while-parked-row"
        data-type={alert.type}
        data-severity={alert.severity}
        data-alert-id={alert.id}
        className={[
          'w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          selected
            ? 'border-raad-primary/40 bg-raad-primary/10'
            : 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={['truncate text-sm', titleClass].join(' ')}>
                {t('alerts.movedWhileParked.title')}
              </span>
              <span
                data-testid="moved-while-parked-tag"
                className="flex-none rounded-md bg-raad-warn/18 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-warn"
              >
                {t('alerts.movedWhileParked.tag')}
              </span>
              {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            </div>

            {plate ? (
              <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
            ) : null}

            <div className="mt-1 text-[12px] text-raad-dim">
              {t('alerts.observedAt', { time: observedTime })}
              {hasDistance ? (
                <>
                  {' · '}
                  <span data-testid="moved-while-parked-distance">
                    {t('alerts.movedWhileParked.distance', { distance: Math.round(distanceM!) })}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}
